// src/utils/deepCleanup.js
import { db, auth } from "@/firebase";
import { collection, getDocs, doc, writeBatch } from "firebase/firestore";

/**
 * Limpia subcolecciones "days" anidadas bajo cada documento de día.
 * Estas subcolecciones son inválidas y no deberían existir.
 * 
 * Ruta inválida: workspaces/{uid}/days/{dayKey}/days/{doc}
 * Ruta válida:   workspaces/{uid}/days/{dayKey}
 */
export async function wipeNestedDaysCollections(workspaceId) {
  console.log(`\n[DEEP_CLEANUP] Limpiando subcolecciones anidadas en workspace: ${workspaceId}`);
  
  try {
    // Obtener todos los documentos de días
    const daysRef = collection(db, "workspaces", workspaceId, "days");
    const daysSnapshot = await getDocs(daysRef);
    
    if (daysSnapshot.empty) {
      console.log("[DEEP_CLEANUP] No hay días para limpiar");
      return { success: true, cleaned: 0 };
    }
    
    let totalCleaned = 0;
    const batch = writeBatch(db);
    let batchCount = 0;
    
    console.log(`[DEEP_CLEANUP] Verificando ${daysSnapshot.size} días...`);
    
    // Para cada día, verificar si tiene subcolección "days" inválida
    for (const dayDoc of daysSnapshot.docs) {
      const dayKey = dayDoc.id;
      
      // Intentar leer subcolección inválida
      const nestedDaysRef = collection(db, "workspaces", workspaceId, "days", dayKey, "days");
      
      try {
        const nestedSnapshot = await getDocs(nestedDaysRef);
        
        if (!nestedSnapshot.empty) {
          console.log(`[DEEP_CLEANUP] ⚠️  Encontrada subcolección inválida bajo ${dayKey}: ${nestedSnapshot.size} documentos`);
          
          // Eliminar cada documento de la subcolección inválida
          nestedSnapshot.docs.forEach(nestedDoc => {
            batch.delete(nestedDoc.ref);
            batchCount++;
            totalCleaned++;
            
            // Firestore limita a 500 operaciones por batch
            if (batchCount >= 500) {
              console.log("[DEEP_CLEANUP] Ejecutando batch (500 operaciones)...");
              batch.commit();
              batchCount = 0;
            }
          });
          
          console.log(`[DEEP_CLEANUP] ✓ Marcados ${nestedSnapshot.size} docs para eliminar bajo ${dayKey}`);
        }
      } catch (error) {
        // Si la subcolección no existe o no se puede acceder, continuar
        if (error.code === 'permission-denied') {
          console.log(`[DEEP_CLEANUP] Acceso denegado a subcolección bajo ${dayKey} (normal si está bloqueada)`);
        } else {
          console.log(`[DEEP_CLEANUP] No se pudo acceder a subcolección bajo ${dayKey} (normal si no existe)`);
        }
      }
    }
    
    // Ejecutar batch final si hay operaciones pendientes
    if (batchCount > 0) {
      console.log(`[DEEP_CLEANUP] Ejecutando batch final (${batchCount} operaciones)...`);
      await batch.commit();
      console.log(`[DEEP_CLEANUP] ✓ Batch final ejecutado`);
    }
    
    if (totalCleaned > 0) {
      console.log(`\n[DEEP_CLEANUP] ✅ Total: ${totalCleaned} documentos anidados eliminados de ${workspaceId}\n`);
    } else {
      console.log(`\n[DEEP_CLEANUP] ✅ No se encontraron subcolecciones inválidas en ${workspaceId}\n`);
    }
    
    return { success: true, cleaned: totalCleaned };
    
  } catch (error) {
    console.error(`[DEEP_CLEANUP] ❌ Error durante limpieza profunda de ${workspaceId}:`, error);
    return { success: false, error: error.message, cleaned: 0 };
  }
}

/**
 * Limpia subcolecciones anidadas del usuario autenticado actual.
 * SOLO se ejecuta cuando se llama manualmente desde la consola.
 * Requiere usuario autenticado.
 */
export async function deepCleanup() {
  // Verificar si hay usuario autenticado
  if (!auth.currentUser) {
    console.log("[DEEP_CLEANUP] skipped: no authenticated user");
    return Promise.resolve({ success: false, reason: "no_auth" });
  }

  const uid = auth.currentUser.uid;
  console.log(`[DEEP_CLEANUP] starting for uid: ${uid}`);

  try {
    const result = await wipeNestedDaysCollections(uid);
    console.log(`[DEEP_CLEANUP] finished`);
    return result;
  } catch (error) {
    console.error(`[DEEP_CLEANUP_ERROR] ${error.message}`);
    return Promise.resolve({ success: false, error: error.message });
  }
}

/**
 * Función de conveniencia para usar desde la consola del navegador
 */
if (typeof window !== 'undefined') {
  window.deepCleanup = deepCleanup;
  console.log("💡 Tip: Puedes ejecutar deepCleanup() desde la consola del navegador");
}
